import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FindUserDto, User, Users } from 'app/core/user/user.types';
import { BehaviorSubject, combineLatest, map, Observable, ReplaySubject, startWith, switchMap, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UserService {
    private _users: ReplaySubject<Users> = new ReplaySubject<Users>(1);
    private _user: ReplaySubject<User> = new ReplaySubject<User>(1);
    private _authUser: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    set user(value: User) {
        this._user.next(value);
    }

    set users(value: User[]) {
        this._users.next(value);
    }

    get user$(): Observable<User> {
        return this._user.asObservable();
    }

    get users$(): Observable<Users> {
        return this._users.asObservable();
    }

    set authUser(value: User | null) {
        this._authUser.next(value);
    }

    get authUser$(): Observable<User | null> {
        return this._authUser.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    get(): Observable<User> {
        return this._httpClient.get<User>('/api/users/own').pipe(
            tap((user) => {
                this._user.next(user);
            })
        );
    }

    getAll(options = new FindUserDto): Observable<Users> {
        let params = new HttpParams();

        if (options && options.query) {
            if (options.query._company && options.query._company.length >= 1) {
                params = params.append(
                    '_company',
                    options.query._company.join(','),
                );
            }

            if (options.query.role && options.query.role.length >= 1) {
                params = params.append(
                    'role',
                    options.query.role.join(','),
                );
            }
        }

        return this._httpClient.get<Users>('/api/users/', { params }).pipe(
            tap((users) => {
                this._users.next(users);
            })
        );
    }

    find(options = new FindUserDto): Observable<Users> {
        let params = new HttpParams();

        if (options && options.query) {
            if (options.query._company && options.query._company.length >= 1) {
                params = params.append(
                    '_company',
                    options.query._company.join(','),
                );
            }

            if (options.query.role && options.query.role.length >= 1) {
                params = params.append(
                    'role',
                    options.query.role.join(','),
                );
            }

            if (options.query.search) {
                params = params.append('search', options.query.search);
            }

            if (options.query.sort) {
                params = params.append('sort', options.query.sort);
            }

            if (options.query.limit !== undefined) {
                params = params.append('limit', String(options.query.limit));
            }

            if (options.query.page !== undefined) {
                params = params.append('page', String(options.query.page));
            }
        }

        return this._httpClient.get<Users>('/api/users/', { params }).pipe(
            tap((users) => {
                this._users.next(users);
            })
        );
    }

    getOne(id: string): Observable<User> {
        return this._httpClient.get<User>(`/api/users/${id}`).pipe(
            tap((user) => {
                this._user.next(user);
            }),
        );
    }

    getInfos(id: string): Observable<User> {
        return this._httpClient.get<User>(`/api/users/infos/${id}`).pipe(
            tap(() => {
            }),
        );
    }

    getDownloadUrl(doc: any, userId: string): Observable<Blob> {
        return this._httpClient.get(`/api/users/download/polo/${doc._id}/${userId}`, {
            responseType: 'blob'
        }).pipe(
            tap(() => console.log('user service download called'))
        );
    }

    create(form: User): Observable<User> {
        return this._httpClient.post<User>(`/api/users/`, form).pipe(
            tap(() => {
                return this.get().subscribe();
            })
        );
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(id: string, user: Partial<User>): Observable<User> {
        return this._httpClient.patch<User>('/api/users/' + id, user).pipe(
            switchMap((response) =>
                combineLatest([
                    this.authUser$.pipe(take(1)),
                    this.user$.pipe(startWith(null as User | null), take(1)),
                    this.users$.pipe(startWith(null as Users | null), take(1)),
                ]).pipe(
                    map(([authUser, currentUser, users]) => {
                        if (authUser && authUser._id === id) {
                            this.authUser = { ...authUser, ...response };
                        }

                        if (users) {
                            const index = users.findIndex(item => item._id === id);

                            if (index >= 0) {
                                users[index] = {
                                    ...users[index],
                                    ...response,
                                };

                                this.users = [...users];
                            }
                        }

                        if (currentUser) {
                            this._user.next(currentUser);
                        }

                        return response;
                    })
                )
            )
        );
    }

    remove(id: string): Observable<void> {
        return this._httpClient.delete<void>(`/api/users/${id}`).pipe(
            tap(() => {
                return this.getAll().subscribe()
            })
        );
    }

    getDisabled(options = new FindUserDto): Observable<Users> {
        let params = new HttpParams();

        if (options && options.query) {
            if (options.query._company && options.query._company.length >= 1) {
                params = params.append(
                    '_company',
                    options.query._company.join(','),
                );
            }
        }

        return this._httpClient.get<Users>('/api/users/disabled', { params });
    }

    reactivate(id: string): Observable<User> {
        return this._httpClient.post<User>(`/api/users/${id}/reactivate`, {});
    }

    uploadAvatar(file: File): Observable<User> {
        const formData = new FormData();
        formData.append('file', file);
        return this._httpClient.post<User>('/api/users/avatar', formData).pipe(
            tap((user) => {
                this._user.next(user);
            })
        );
    }

    importUsers(users: any[]): Observable<any> {
        return this._httpClient.post<any>('/api/users/import', { users }).pipe(
            tap(() => {
                this.getAll().subscribe();
            })
        );
    }

}
